import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

const MINIMUM_PAINTED_FRAME_SIZE = 2
// Stripe can cycle through several non-final iframe heights on a cold load.
export const STRIPE_CONNECT_STABLE_PAINT_MS = 350

function getPaintedFrameSize(container: HTMLElement | null | undefined) {
  const frame = container?.querySelector('iframe')

  if (!frame) {
    return null
  }

  const bounds = frame.getBoundingClientRect()

  if (bounds.width < MINIMUM_PAINTED_FRAME_SIZE || bounds.height < MINIMUM_PAINTED_FRAME_SIZE) {
    return null
  }

  return {
    height: Math.round(bounds.height),
    width: Math.round(bounds.width),
  }
}

interface SurfaceDisplayProps {
  className?: string
  minHeightClassName?: string
  placeholder?: ReactNode
  renderAttemptKey?: number | null
  showPlaceholder?: boolean
}

type ActiveSurfaceProps<TLoaderEvent> = {
  children?:
    | ((callbacks: { handleLoaderStart: (event: TLoaderEvent) => void }) => ReactNode)
    | null
    | false
  onLoaderStart: (event: TLoaderEvent) => void
  onSurfacePainted: () => void
}

type PlaceholderSurfaceProps = {
  children?: null | false
  onLoaderStart?: undefined
  onSurfacePainted?: undefined
}

type PaintedSurfaceProps<TLoaderEvent> = SurfaceDisplayProps &
  (ActiveSurfaceProps<TLoaderEvent> | PlaceholderSurfaceProps)

export default function StripeConnectPaintedSurface<TLoaderEvent>({
  children,
  className,
  minHeightClassName,
  onLoaderStart,
  onSurfacePainted,
  placeholder,
  renderAttemptKey,
  showPlaceholder,
}: PaintedSurfaceProps<TLoaderEvent>) {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const stopWatchingRef = useRef(() => {})
  const [loaderStartSignal, setLoaderStartSignal] = useState<{
    generation: number
    renderAttemptKey: number | null | undefined
  }>({
    generation: 0,
    renderAttemptKey: null,
  })

  const stopWatching = useCallback(() => {
    stopWatchingRef.current()
    stopWatchingRef.current = () => {}
  }, [])

  useEffect(() => stopWatching, [renderAttemptKey, stopWatching])

  const waitForPaint = useCallback(() => {
    stopWatching()

    const content = contentRef.current

    if (!content || !onSurfacePainted) {
      return
    }

    let firstFrameId: number | null = null
    let secondFrameId: number | null = null
    let stablePaintTimeoutId: number | null = null
    let resizeObserver: ResizeObserver | null = null
    let mutationObserver: MutationObserver | null = null
    let observedFrame: HTMLIFrameElement | null = null

    const cancelPaintConfirmation = () => {
      if (stablePaintTimeoutId !== null) {
        window.clearTimeout(stablePaintTimeoutId)
        stablePaintTimeoutId = null
      }

      if (firstFrameId !== null) {
        window.cancelAnimationFrame(firstFrameId)
        firstFrameId = null
      }

      if (secondFrameId !== null) {
        window.cancelAnimationFrame(secondFrameId)
        secondFrameId = null
      }
    }

    const cleanup = () => {
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
      cancelPaintConfirmation()
    }

    const confirmPaint = () => {
      cancelPaintConfirmation()

      const candidateSize = getPaintedFrameSize(content)

      if (!candidateSize) {
        return
      }

      stablePaintTimeoutId = window.setTimeout(() => {
        stablePaintTimeoutId = null
        firstFrameId = window.requestAnimationFrame(() => {
          firstFrameId = null
          secondFrameId = window.requestAnimationFrame(() => {
            secondFrameId = null
            const paintedSize = getPaintedFrameSize(content)

            if (
              !paintedSize ||
              paintedSize.height !== candidateSize.height ||
              paintedSize.width !== candidateSize.width
            ) {
              confirmPaint()
              return
            }

            cleanup()
            stopWatchingRef.current = () => {}
            onSurfacePainted()
          })
        })
      }, STRIPE_CONNECT_STABLE_PAINT_MS)
    }

    const observeFrame = () => {
      const frame = content.querySelector('iframe')

      if (!frame || frame === observedFrame) {
        return
      }

      observedFrame = frame
      resizeObserver?.disconnect()
      resizeObserver = new ResizeObserver(confirmPaint)
      resizeObserver.observe(frame)
      confirmPaint()
    }

    mutationObserver = new MutationObserver(observeFrame)
    mutationObserver.observe(content, { childList: true, subtree: true })
    observeFrame()

    stopWatchingRef.current = cleanup
  }, [onSurfacePainted, stopWatching])

  const handleLoaderStart = useCallback(
    (event: TLoaderEvent) => {
      if (!onLoaderStart) return

      onLoaderStart(event)
      setLoaderStartSignal(({ generation }) => ({
        generation: generation + 1,
        renderAttemptKey,
      }))
    },
    [onLoaderStart, renderAttemptKey]
  )

  useEffect(() => {
    if (loaderStartSignal.renderAttemptKey !== renderAttemptKey) {
      return
    }

    waitForPaint()
  }, [loaderStartSignal, renderAttemptKey, waitForPaint])

  return (
    <div
      className={cn(
        'relative grid rounded-md',
        showPlaceholder ? 'overflow-hidden' : 'overflow-visible',
        showPlaceholder && minHeightClassName,
        className
      )}
    >
      {children ? (
        <div
          ref={contentRef}
          aria-hidden={showPlaceholder || undefined}
          className={cn(
            'col-start-1 row-start-1 min-w-0',
            // Size containment keeps Stripe's transient iframe heights from stretching the skeleton.
            showPlaceholder && 'pointer-events-none [contain:size]'
          )}
        >
          {children({ handleLoaderStart })}
        </div>
      ) : null}

      {showPlaceholder && placeholder ? (
        <div className="z-10 col-start-1 row-start-1 min-h-[inherit] bg-card">{placeholder}</div>
      ) : null}
    </div>
  )
}
